import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Save } from "lucide-react";
import { toast } from "sonner";
import { DocumentUploadAdmin } from "@/components/admin/DocumentUploadAdmin";

const API_URL = "/api";

const EditShipment = () => {
    const navigate = useNavigate();
    const { trackingNumber } = useParams();
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [formData, setFormData] = useState({
        sender_name: "",
        sender_company: "",
        sender_address: "",
        sender_city: "",
        sender_country: "",
        sender_postal_code: "",
        sender_phone: "",
        sender_email: "",
        receiver_name: "",
        receiver_company: "",
        receiver_address: "",
        receiver_city: "",
        receiver_country: "",
        receiver_postal_code: "",
        receiver_phone: "",
        receiver_email: "",
        package_name: "",
        service_type: "Standard Shipping",
        weight: "",
        length: "",
        width: "",
        height: "",
        pieces: "1",
        declared_value: "",
        currency: "EUR",
        description: "",
        origin: "",
        destination: "",
        vessel_name: "",
        estimated_delivery: "",
        current_status: "Processing",
        container_number: "",
        cargo_type: "",
        container_type: "",
        shipping_mode: "",
        incoterm: "",
        commodity: "",
        reference_number: "",
        current_lat: "",
        current_lng: "",
        next_stop: "",
        distance_remaining: "",
    });

    useEffect(() => {
        if (!localStorage.getItem("admin_logged_in")) {
            navigate("/admin");
            return;
        }

        fetchShipment();
    }, [navigate, trackingNumber]);

    const fetchShipment = async () => {
        setFetching(true);
        try {
            const response = await fetch(
                `${API_URL}/shipments.php?tracking_number=${trackingNumber}`,
                { credentials: "include" }
            );

            const data = await response.json();

            if (data.success) {
                const shipment = data.data;
                setFormData({
                    sender_name: shipment.sender_name || "",
                    sender_company: shipment.sender_company || "",
                    sender_address: shipment.sender_address || "",
                    sender_city: shipment.sender_city || "",
                    sender_country: shipment.sender_country || "",
                    sender_postal_code: shipment.sender_postal_code || "",
                    sender_phone: shipment.sender_phone || "",
                    sender_email: shipment.sender_email || "",
                    receiver_name: shipment.receiver_name || "",
                    receiver_company: shipment.receiver_company || "",
                    receiver_address: shipment.receiver_address || "",
                    receiver_city: shipment.receiver_city || "",
                    receiver_country: shipment.receiver_country || "",
                    receiver_postal_code: shipment.receiver_postal_code || "",
                    receiver_phone: shipment.receiver_phone || "",
                    receiver_email: shipment.receiver_email || "",
                    package_name: shipment.package_name || "",
                    service_type: shipment.service_type || "Standard Shipping",
                    weight: shipment.weight || "",
                    length: shipment.length || "",
                    width: shipment.width || "",
                    height: shipment.height || "",
                    pieces: shipment.pieces || "1",
                    declared_value: shipment.declared_value || "",
                    currency: shipment.currency || "EUR",
                    description: shipment.description || "",
                    origin: shipment.origin || "",
                    destination: shipment.destination || "",
                    vessel_name: shipment.vessel_name || "",
                    estimated_delivery: shipment.estimated_delivery || "",
                    current_status: shipment.current_status || "Processing",
                });
            } else {
                toast.error("Shipment not found");
                navigate("/admin/dashboard");
            }
        } catch (error) {
            console.error("Fetch error:", error);
            toast.error("Failed to load shipment");
            navigate("/admin/dashboard");
        } finally {
            setFetching(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        try {
            const response = await fetch(`${API_URL}/shipments.php`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: "include",
                body: JSON.stringify({
                    tracking_number: trackingNumber,
                    ...formData,
                }),
            });

            const data = await response.json();

            if (data.success) {
                toast.success("Shipment updated successfully");
                navigate("/admin/dashboard");
            } else {
                toast.error(data.error || "Failed to update shipment");
            }
        } catch (error) {
            console.error("Update error:", error);
            toast.error("Failed to update shipment");
        } finally {
            setLoading(false);
        }
    };

    const updateField = (field: string, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    if (fetching) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="text-center">
                    <p className="text-xl">Loading shipment data...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            <div className="border-b bg-white sticky top-0 z-10">
                <div className="container mx-auto px-4 py-4">
                    <div className="flex items-center gap-4">
                        <Button variant="outline" onClick={() => navigate("/admin/dashboard")}>
                            <ArrowLeft className="h-4 w-4 mr-2" />
                            Back
                        </Button>
                        <div>
                            <h1 className="text-2xl font-bold">Edit Shipment</h1>
                            <p className="text-sm text-muted-foreground font-mono">{trackingNumber}</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container mx-auto px-4 py-8">
                <form onSubmit={handleSubmit}>
                    <div className="grid gap-6">
                        {/* Same form structure as AddShipment */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Sender Information</CardTitle>
                            </CardHeader>
                            <CardContent className="grid md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="sender_name">Full Name *</Label>
                                    <Input
                                        id="sender_name"
                                        required
                                        value={formData.sender_name}
                                        onChange={(e) => updateField("sender_name", e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="sender_company">Company</Label>
                                    <Input
                                        id="sender_company"
                                        value={formData.sender_company}
                                        onChange={(e) => updateField("sender_company", e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2 md:col-span-2">
                                    <Label htmlFor="sender_address">Address *</Label>
                                    <Input
                                        id="sender_address"
                                        required
                                        value={formData.sender_address}
                                        onChange={(e) => updateField("sender_address", e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="sender_city">City *</Label>
                                    <Input
                                        id="sender_city"
                                        required
                                        value={formData.sender_city}
                                        onChange={(e) => updateField("sender_city", e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="sender_country">Country *</Label>
                                    <Input
                                        id="sender_country"
                                        required
                                        value={formData.sender_country}
                                        onChange={(e) => updateField("sender_country", e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="sender_postal_code">Postal Code *</Label>
                                    <Input
                                        id="sender_postal_code"
                                        required
                                        value={formData.sender_postal_code}
                                        onChange={(e) => updateField("sender_postal_code", e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="sender_phone">Phone *</Label>
                                    <Input
                                        id="sender_phone"
                                        required
                                        value={formData.sender_phone}
                                        onChange={(e) => updateField("sender_phone", e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2 md:col-span-2">
                                    <Label htmlFor="sender_email">Email</Label>
                                    <Input
                                        id="sender_email"
                                        type="email"
                                        value={formData.sender_email}
                                        onChange={(e) => updateField("sender_email", e.target.value)}
                                    />
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle>Receiver Information</CardTitle>
                            </CardHeader>
                            <CardContent className="grid md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="receiver_name">Full Name *</Label>
                                    <Input
                                        id="receiver_name"
                                        required
                                        value={formData.receiver_name}
                                        onChange={(e) => updateField("receiver_name", e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="receiver_company">Company</Label>
                                    <Input
                                        id="receiver_company"
                                        value={formData.receiver_company}
                                        onChange={(e) => updateField("receiver_company", e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2 md:col-span-2">
                                    <Label htmlFor="receiver_address">Address *</Label>
                                    <Input
                                        id="receiver_address"
                                        required
                                        value={formData.receiver_address}
                                        onChange={(e) => updateField("receiver_address", e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="receiver_city">City *</Label>
                                    <Input
                                        id="receiver_city"
                                        required
                                        value={formData.receiver_city}
                                        onChange={(e) => updateField("receiver_city", e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="receiver_country">Country *</Label>
                                    <Input
                                        id="receiver_country"
                                        required
                                        value={formData.receiver_country}
                                        onChange={(e) => updateField("receiver_country", e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="receiver_postal_code">Postal Code *</Label>
                                    <Input
                                        id="receiver_postal_code"
                                        required
                                        value={formData.receiver_postal_code}
                                        onChange={(e) => updateField("receiver_postal_code", e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="receiver_phone">Phone *</Label>
                                    <Input
                                        id="receiver_phone"
                                        required
                                        value={formData.receiver_phone}
                                        onChange={(e) => updateField("receiver_phone", e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2 md:col-span-2">
                                    <Label htmlFor="receiver_email">Email</Label>
                                    <Input
                                        id="receiver_email"
                                        type="email"
                                        value={formData.receiver_email}
                                        onChange={(e) => updateField("receiver_email", e.target.value)}
                                    />
                                </div>
                            </CardContent>
                        </Card>

                        {/* Extended Details */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Shipment Extended Details</CardTitle>
                            </CardHeader>
                            <CardContent className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="container_number">Container Number</Label>
                                    <Input
                                        id="container_number"
                                        value={formData.container_number || ""}
                                        onChange={(e) => updateField("container_number", e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="cargo_type">Cargo Type</Label>
                                    <Input
                                        id="cargo_type"
                                        value={formData.cargo_type || ""}
                                        onChange={(e) => updateField("cargo_type", e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="container_type">Container Type</Label>
                                    <Input
                                        id="container_type"
                                        value={formData.container_type || ""}
                                        onChange={(e) => updateField("container_type", e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="shipping_mode">Shipping Mode</Label>
                                    <Input
                                        id="shipping_mode"
                                        value={formData.shipping_mode || ""}
                                        onChange={(e) => updateField("shipping_mode", e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="incoterm">Incoterm</Label>
                                    <Input
                                        id="incoterm"
                                        value={formData.incoterm || ""}
                                        onChange={(e) => updateField("incoterm", e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="commodity">Commodity</Label>
                                    <Input
                                        id="commodity"
                                        value={formData.commodity || ""}
                                        onChange={(e) => updateField("commodity", e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="reference_number">Reference No</Label>
                                    <Input
                                        id="reference_number"
                                        value={formData.reference_number || ""}
                                        onChange={(e) => updateField("reference_number", e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="current_lat">Current Lat</Label>
                                    <Input
                                        id="current_lat"
                                        type="number" step="0.00000001"
                                        value={formData.current_lat || ""}
                                        onChange={(e) => updateField("current_lat", e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="current_lng">Current Lng</Label>
                                    <Input
                                        id="current_lng"
                                        type="number" step="0.00000001"
                                        value={formData.current_lng || ""}
                                        onChange={(e) => updateField("current_lng", e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="next_stop">Next Stop</Label>
                                    <Input
                                        id="next_stop"
                                        value={formData.next_stop || ""}
                                        onChange={(e) => updateField("next_stop", e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="distance_remaining">Distance Remaining</Label>
                                    <Input
                                        id="distance_remaining"
                                        value={formData.distance_remaining || ""}
                                        onChange={(e) => updateField("distance_remaining", e.target.value)}
                                    />
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle>Shipment Details</CardTitle>
                            </CardHeader>
                            <CardContent className="grid md:grid-cols-2 gap-4">
                                <div className="space-y-2 md:col-span-2">
                                    <Label htmlFor="package_name">Package Name / Content *</Label>
                                    <Input
                                        id="package_name"
                                        required
                                        placeholder="e.g. Electronics, Documents, Machinery Parts"
                                        value={formData.package_name}
                                        onChange={(e) => updateField("package_name", e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="service_type">Service Type *</Label>
                                    <Select
                                        value={formData.service_type}
                                        onValueChange={(value) => updateField("service_type", value)}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="Standard Shipping">Standard Shipping</SelectItem>
                                            <SelectItem value="Express Shipping">Express Shipping</SelectItem>
                                            <SelectItem value="Ocean Freight FCL">Ocean Freight FCL</SelectItem>
                                            <SelectItem value="Ocean Freight LCL">Ocean Freight LCL</SelectItem>
                                            <SelectItem value="Air Freight">Air Freight</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="weight">Weight (KG) *</Label>
                                    <Input
                                        id="weight"
                                        type="number"
                                        step="0.01"
                                        required
                                        value={formData.weight}
                                        onChange={(e) => updateField("weight", e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="length">Length (CM)</Label>
                                    <Input
                                        id="length"
                                        type="number"
                                        step="0.01"
                                        value={formData.length}
                                        onChange={(e) => updateField("length", e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="width">Width (CM)</Label>
                                    <Input
                                        id="width"
                                        type="number"
                                        step="0.01"
                                        value={formData.width}
                                        onChange={(e) => updateField("width", e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="height">Height (CM)</Label>
                                    <Input
                                        id="height"
                                        type="number"
                                        step="0.01"
                                        value={formData.height}
                                        onChange={(e) => updateField("height", e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="pieces">NO. of Units/Pieces *</Label>
                                    <Input
                                        id="pieces"
                                        type="number"
                                        required
                                        value={formData.pieces}
                                        onChange={(e) => updateField("pieces", e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="declared_value">Declared Value</Label>
                                    <Input
                                        id="declared_value"
                                        type="number"
                                        step="0.01"
                                        value={formData.declared_value}
                                        onChange={(e) => updateField("declared_value", e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="currency">Currency</Label>
                                    <Select
                                        value={formData.currency}
                                        onValueChange={(value) => updateField("currency", value)}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="EUR">EUR (€)</SelectItem>
                                            <SelectItem value="USD">USD ($)</SelectItem>
                                            <SelectItem value="GBP">GBP (£)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2 md:col-span-2">
                                    <Label htmlFor="description">Description</Label>
                                    <Textarea
                                        id="description"
                                        value={formData.description}
                                        onChange={(e) => updateField("description", e.target.value)}
                                    />
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle>Shipping Information</CardTitle>
                            </CardHeader>
                            <CardContent className="grid md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="origin">Origin *</Label>
                                    <Input
                                        id="origin"
                                        required
                                        value={formData.origin}
                                        onChange={(e) => updateField("origin", e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="destination">Destination *</Label>
                                    <Input
                                        id="destination"
                                        required
                                        value={formData.destination}
                                        onChange={(e) => updateField("destination", e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="vessel_name">Vessel Name</Label>
                                    <Input
                                        id="vessel_name"
                                        value={formData.vessel_name}
                                        onChange={(e) => updateField("vessel_name", e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="estimated_delivery">Estimated Delivery</Label>
                                    <Input
                                        id="estimated_delivery"
                                        type="date"
                                        value={formData.estimated_delivery}
                                        onChange={(e) => updateField("estimated_delivery", e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="current_status">Current Status *</Label>
                                    <Select
                                        value={formData.current_status}
                                        onValueChange={(value) => updateField("current_status", value)}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="Processing">Processing</SelectItem>
                                            <SelectItem value="Booked">Booked</SelectItem>
                                            <SelectItem value="Container Loaded">Container Loaded</SelectItem>
                                            <SelectItem value="Departed">Departed</SelectItem>
                                            <SelectItem value="In Transit">In Transit</SelectItem>
                                            <SelectItem value="On Hold">On Hold</SelectItem>
                                            <SelectItem value="Customs Clearance">Customs Clearance</SelectItem>
                                            <SelectItem value="Out for Delivery">Out for Delivery</SelectItem>
                                            <SelectItem value="Delivered">Delivered</SelectItem>
                                            <SelectItem value="Exception">Exception</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </CardContent>
                        </Card>

                        <div className="flex gap-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => navigate("/admin/dashboard")}
                                disabled={loading}
                            >
                                Cancel
                            </Button>
                            <Button type="submit" disabled={loading}>
                                <Save className="h-4 w-4 mr-2" />
                                {loading ? "Updating..." : "Update Shipment"}
                            </Button>
                        </div>
                    </div>
                </form>

                {/* Documents Upload Section */}
                <DocumentUploadAdmin trackingNumber={trackingNumber || ""} />
            </div>
        </div>
    );
};

export default EditShipment;
